import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  Box,
  Divider,
  Chip,
  TablePagination,
  Paper,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  getLabourRates,
  deleteLabourCostRate,
  deleteLabourBillingRate,
  Role,
  LabourCostRate,
  LabourBillingRate,
} from 'api/labourApi';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';
import ConfirmDeleteDialog from 'components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import { useSnackbar } from 'notistack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  RATE_CARD_WORKING_AWAY_COST_HEADER_TOOLTIP,
  WORKING_AWAY_INFO_ARIA_LABEL,
  WORKING_AWAY_TOOLTIP_TYPOGRAPHY_SX,
} from 'constants/workingAwayFromHomeCopy';

interface LabourDetailsViewProps {
  handleClose: () => void;
  selectedRole?: Role;
  rateType: 'cost' | 'billing';
  open: boolean;
  onEditCostRate?: (rate: LabourCostRate) => void;
  onEditBillingRate?: (rate: LabourBillingRate) => void;
}

export default function LabourDetailsView({
  handleClose,
  selectedRole,
  rateType,
  open,
  onEditCostRate,
  onEditBillingRate,
}: LabourDetailsViewProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<
    LabourCostRate | LabourBillingRate | null
  >(null);

  const queryClient = useQueryClient();
  const { data: labourData } = useQuery(['labour-rates'], getLabourRates);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const deleteCostMutation = useMutation(deleteLabourCostRate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['labour-rates']);
      enqueueSnackbar('Labour cost rate deleted successfully', {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar('Failed to delete labour cost rate', {
        variant: 'error',
      });
    },
  });

  const deleteBillingMutation = useMutation(deleteLabourBillingRate, {
    onSuccess: () => {
      queryClient.invalidateQueries(['labour-rates']);
      enqueueSnackbar('Labour billing rate deleted successfully', {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar('Failed to delete labour billing rate', {
        variant: 'error',
      });
    },
  });

  const handleDelete = async (rateId: string) => {
    if (rateType === 'cost') {
      await deleteCostMutation.mutateAsync(rateId);
    } else {
      await deleteBillingMutation.mutateAsync(rateId);
    }
    setDeleteDialogOpen(false);
  };

  const displayRates =
    rateType === 'cost'
      ? labourData?.costRates.filter((r) => r.role === selectedRole) || []
      : labourData?.billingRates.filter((r) => r.role === selectedRole) || [];

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            my: 1,
            display: 'flex',
            justifyContent: 'space-between',
          }}>
          <Box display="flex" alignItems="center">
            <Typography variant="h6">
              {rateType === 'cost'
                ? 'Labour Cost Rate History'
                : 'Labour Billing Rate History'}
            </Typography>
            <Chip
              label={selectedRole}
              size="small"
              sx={{ ml: 2, backgroundColor: 'primary.main', color: 'white' }}
            />
          </Box>
          <IconButton size="small" onClick={handleClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent
          sx={{
            mb: 2,
          }}>
          <Box>
            <Paper elevation={0}>
              <Table size="medium">
                <TableHead>
                  <TableRow>
                    {rateType === 'cost' ? (
                      <>
                        <TableCell>Rate</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.25,
                            }}>
                            Working Away From Home Rate
                            <Tooltip
                              arrow
                              title={
                                <Typography
                                  component="div"
                                  variant="body2"
                                  sx={{
                                    ...WORKING_AWAY_TOOLTIP_TYPOGRAPHY_SX,
                                    maxWidth: 280,
                                  }}>
                                  {RATE_CARD_WORKING_AWAY_COST_HEADER_TOOLTIP}
                                </Typography>
                              }>
                              <IconButton
                                size="small"
                                aria-label={WORKING_AWAY_INFO_ARIA_LABEL}
                                sx={{ p: 0.25 }}>
                                <InfoOutlinedIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>Billing Rate</TableCell>
                        <TableCell>Working Away From Home Billing Rate</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Branch</TableCell>
                      </>
                    )}
                    <TableCell>Effective From</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayRates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={rateType === 'cost' ? 4 : 6}>
                        <Typography align="center" variant="body2">
                          No rate history available.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {displayRates
                        .sort((a, b) => {
                          if (a.isDefault && !b.isDefault) return -1; // "Default" comes first
                          if (!a.isDefault && b.isDefault) return 1; // "Default" comes first
                          return (
                            new Date(b.effectiveFrom).getTime() -
                            new Date(a.effectiveFrom).getTime()
                          ); // Sort by effective date
                        })
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((rate) => (
                          <TableRow key={rate.id}>
                            {rateType === 'cost' ? (
                              <>
                                <TableCell>
                                  ${Number((rate as any).rate).toFixed(2)}
                                </TableCell>
                                <TableCell>
                                  $
                                  {Number(
                                    (rate as any).workingAwayFromHomeRate
                                  ).toFixed(2)}
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell>
                                  ${Number(rate.billingRate).toFixed(2)}
                                  {rate.isDefault && (
                                    <Chip
                                      label="Default"
                                      size="small"
                                      sx={{
                                        ml: 1,
                                        backgroundColor: 'grey.500',
                                        color: 'white',
                                      }}
                                    />
                                  )}
                                </TableCell>
                                <TableCell>
                                  $
                                  {Number(
                                    (rate as LabourBillingRate)
                                      .workingAwayFromHomeBillingRate ?? 0
                                  ).toFixed(2)}
                                </TableCell>
                                <TableCell>
                                  {rate.client?.legalName || '-'}
                                </TableCell>
                                <TableCell>
                                  {rate.branch?.name || '-'}
                                </TableCell>
                              </>
                            )}
                            <TableCell>
                              {format(
                                new Date(rate.effectiveFrom),
                                'dd MMM yyyy'
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {rateType === 'cost' && onEditCostRate && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEditCostRate(rate as LabourCostRate);
                                  }}
                                  aria-label="Edit cost rate">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                              {rateType === 'billing' && onEditBillingRate && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEditBillingRate(
                                      rate as LabourBillingRate
                                    );
                                  }}
                                  aria-label="Edit billing rate">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                sx={{ ml: 1 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedItem(rate);
                                  setDeleteDialogOpen(true);
                                }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                    </>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={displayRates.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          </Box>
        </DialogContent>
      </Dialog>
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        title="Delete Confirmation"
        content={
          <>
            Are you sure you want to delete the item of{' '}
            <b>{selectedItem?.role}</b>?
            <Alert severity="warning" style={{ marginTop: '1rem' }}>
              This action is irreversible and will permanently delete the data.
            </Alert>
          </>
        }
        deleteFunc={async () => {
          await handleDelete(selectedItem?.id);
        }}
        handleReject={() => {
          setDeleteDialogOpen(false);
          setSelectedItem(null);
        }}
        handleClose={() => {
          setDeleteDialogOpen(false);
          setSelectedItem(null);
        }}
      />
    </>
  );
}
